import Image from 'next/image';
import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useTrackDisabledSelectAll } from '@air/analytics';
import { Board, Close } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';
import { type SelectableItem, selectedItemsCountSelector, unselectItemAction } from '@air/redux-selected-items';
import { formatBytes } from '@air/utilities';

import canvasCardPlaceholder from '~/assets/canvas-card-placeholder.png';
import BoardThumbnail from '~/components/BoardThumbnail/BoardThumbnail';
import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { isSelectableGalleryItem } from '~/store/selectedItems/types';
import { ClipIconSize } from '~/types/clip-icon-size';
import { useAirStore } from '~/utils/ReduxUtils';

export type SelectedItemProps = {
  item: SelectableItem;
  allowUnselect?: boolean;
};

export const SelectedItem = memo(({ item, allowUnselect = true }: SelectedItemProps) => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);

  const handleUnselect = useCallback(() => {
    if (selectAll) {
      trackDisabledSelectAll({
        trigger: 'item-unselect',
        totalSelectionCount: selectedItemsCountSelector(store.getState()),
      });
    }
    resetSelectAll();
    dispatch(unselectItemAction({ itemId: item.id }));
  }, [dispatch, item.id, resetSelectAll, selectAll, store, trackDisabledSelectAll]);

  // Acts as type-guard for subsequent logic
  if (!isSelectableGalleryItem(item)) {
    return null;
  }

  if (item.type === 'board' || item.type === 'topLevelBoard') {
    const board = item.item;

    return (
      <div className="flex flex-row gap-2 px-3 py-2">
        <div className="shrink-0 rounded-8 p-1 outline outline-1 outline-grey-5">
          <BoardThumbnail board={board} height={40} objectFit="cover" sizes="100vw" width={72} />
        </div>
        <div className="flex flex-col justify-center gap-0.5">
          <div className="truncate font-medium text-14 text-grey-12">{board.title}</div>
          <div className="flex items-center gap-1 font-medium text-10 text-grey-12 uppercase">
            <Board className="size-3 shrink-0" />
            Board
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center justify-center">
          {allowUnselect && (
            <IconButton
              icon={Close}
              onClick={handleUnselect}
              label="Unselect item"
              appearance="ghost"
              color="grey"
              size="small"
            />
          )}
        </div>
      </div>
    );
  }

  if (item.type === 'asset' || item.type === 'file') {
    const clip = item.item;
    const clipTitle = clip.title || clip.importedName || 'Untitled';
    const metadata = [clip.ext, formatBytes(clip.size)].join(' · ');

    return (
      <div className="flex flex-row gap-2 px-3 py-2">
        <div className="relative h-12 w-20 shrink-0">
          <ClipThumbnail
            {...clip}
            width={80}
            height={48}
            src={clip.assets.image}
            iconSize={ClipIconSize.large}
            size="mediumSmall"
            style={{
              width: 80,
              height: 48,
              borderRadius: 8,
            }}
          />
        </div>
        <div className="flex flex-col justify-center gap-0.5">
          <div className="truncate font-medium text-14 text-grey-12">{clipTitle}</div>
          <div className="flex gap-1 truncate font-medium text-10 text-grey-12 uppercase">{metadata}</div>
        </div>
        <div className="ml-auto flex shrink-0 items-center justify-center">
          {allowUnselect && (
            <IconButton
              icon={Close}
              onClick={handleUnselect}
              label="Unselect item"
              appearance="ghost"
              color="grey"
              size="small"
            />
          )}
        </div>
      </div>
    );
  }

  if (item.type === 'canvas') {
    const canvas = item.item;
    const canvasTitle = canvas.title || 'Untitled';

    return (
      <div className="flex flex-row gap-2 px-3 py-2">
        <div className="relative h-12 w-20 shrink-0 overflow-hidden rounded-6 bg-grey-3">
          <Image
            alt={canvasTitle}
            className="size-full object-cover object-center"
            height={48}
            src={canvasCardPlaceholder.src}
            width={80}
          />
        </div>
        <div className="flex flex-col justify-center gap-0.5">
          <div className="truncate font-medium text-14 text-grey-12">{canvasTitle}</div>
          <div className="flex gap-1 truncate font-medium text-10 text-grey-12 uppercase">Canvas</div>
        </div>
        <div className="ml-auto flex shrink-0 items-center justify-center">
          {allowUnselect && (
            <IconButton
              icon={Close}
              onClick={handleUnselect}
              label="Unselect item"
              appearance="ghost"
              color="grey"
              size="small"
            />
          )}
        </div>
      </div>
    );
  }

  return null;
});

SelectedItem.displayName = 'SelectedItem';
